import {
  createContext,
  useEffect,
  useState,
  ReactNode,
  useContext,
} from "react";
import SnackbarAlert from "components/elements/SnackbarAlert";
import { useLogout } from "./LogoutProvider";
import useWSMApi from "./api/useWSMApi";
import { STORAGE_KEY } from "utils/strings";

interface StorageContextValue {
  userProfileData: any;
  membershipListData: any;
  creditPlanListData: any;
  refreshAppData: () => void;
  refreshUserProfile: () => void;
  updateUserProfileData: (data: any) => void;
}

const StorageContext = createContext<StorageContextValue | undefined>(
  undefined,
);

export const useStorage = (): StorageContextValue => {
  const context = useContext(StorageContext);
  if (!context) {
    throw new Error("useStorage must be used within a StorageProvider");
  }
  return context;
};

interface StorageProviderProps {
  children: ReactNode;
}

export const StorageProvider: React.FC<StorageProviderProps> = ({
  children,
}) => {
  const { fetchUserProfile, fetchMembershipList, fetchCreditPlans } =
    useWSMApi();
  const [userProfileData, setUserProfileData] = useState<any>(null);
  const [membershipListData, setMembershipListData] = useState<any>(null);
  const [creditPlanListData, setCreditPlanListData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const { handleUnauthorized } = useLogout();

  const refreshUserProfile = (): void => {
    fetchUserProfile()
      .then((data: any) => {
        localStorage.setItem(
          STORAGE_KEY.USER_PROFILE_DATA,
          JSON.stringify(data),
        );
        setUserProfileData(data);
      })
      .catch((err: any) => setError(err));
  };

  const updateUserProfileData = (data: any): void => {
    localStorage.setItem(STORAGE_KEY.USER_PROFILE_DATA, JSON.stringify(data));
    setUserProfileData(data);
  };

  const refreshAppData = (): void => {
    fetchMembershipList()
      .then((data: any) => setMembershipListData(data))
      .catch((err: any) => setError(err));

    fetchCreditPlans()
      .then((data: any) => setCreditPlanListData(data))
      .catch((err: any) => setError(err));
  };

  useEffect(() => {
    const profileData = localStorage.getItem(STORAGE_KEY.USER_PROFILE_DATA);

    if (profileData) {
      refreshUserProfile();
      refreshAppData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    if (error.status === 404) {
      handleUnauthorized();
      return null;
    }
    return <SnackbarAlert type="error">{error.message}</SnackbarAlert>;
  }

  return (
    <StorageContext.Provider
      value={{
        userProfileData,
        membershipListData,
        creditPlanListData,
        refreshAppData,
        refreshUserProfile,
        updateUserProfileData,
      }}
    >
      {children}
    </StorageContext.Provider>
  );
};
